import {
  AlternativeCalculationDialogState,
  CaseCalculationAlternativePart,
  CaseCalculationHistory,
  CaseCalculationPart,
  PartitionedCalculationParts
} from '../../../interfaces/Case';
import { WorkflowCalculation, WorkflowSupplier } from '../../../interfaces/Workflow';
import { IReferential } from '../../../interfaces/IReferential';
import { Utils } from '../../../helpers/Utils';
import { Organization } from '../../../interfaces/Organization';
import { CalculationParts } from '../../../interfaces/CalculationParts';
import { mapAlternativePartToCalculationPart } from '../../../utils/calculationPartUtils';
import {
  CalculationSectionCodeEnum,
  CarPartQualityRatings,
  CarPartQualityTypeCode
} from '../../../helpers/Constants';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import { GRID_COLUMNS } from '../config/columns';
import {
  CalculationTotals,
  ColumnFilter,
  ColumnKey,
  ColumnType,
  FiltersState,
  isDateRangeFilter,
  isRangeFilter,
  SortState,
  VariantSelectionState
} from '../types/alternative-calculation.types';

export const uuidToNumber = (): number => {
  const uniqueId = uuid();
  const hexString = uniqueId.replace(/-/g, '');
  const numericValue = parseInt(hexString, 16);

  return numericValue % 1000000000;
};

export const updatePart = <T extends CaseCalculationPart>(
  parts: T[],
  partId: number,
  fn: (part: T) => T
): T[] => parts.map((p) => (p.id === partId ? fn(p) : p));

// Own-stock PART with no supplier/alternative and zero price — needs a manual price.
export const isZeroPriceOwnStockPart = (part: CaseCalculationPart): boolean =>
  part.alternativeParts.length === 0 &&
  part.calculationSection!.code === CalculationSectionCodeEnum.PART &&
  !part.isOrdered &&
  !part.supplierId &&
  part.originalPrice === 0;

export const isInterogablePart = (part: CaseCalculationPart): boolean =>
  part.calculationSection!.code === CalculationSectionCodeEnum.PART && !part.isOrdered;

export const packingErrorQuantity = (
  pieceNr: number,
  minQty: number,
  packing: string | null,
  groupPieceNr?: number
): number | null => {
  const [first, second] = (packing ?? '').split('|');
  if (first && second) {
    const packingNumber = parseFloat(first);
    if (groupPieceNr != null && groupPieceNr % packingNumber === 0) return null;
    if (pieceNr % packingNumber !== 0 || pieceNr < minQty) return packingNumber;
    return null;
  }
  if (pieceNr < minQty) return minQty;
  return null;
};

export const isNonStockDescriptionMissing = (
  workflowCalculation: WorkflowCalculation | null,
  nonStockSuppliers: WorkflowSupplier[],
  supplierId: number | null | undefined,
  isInStock: boolean,
  description: string | null | undefined
): boolean => {
  if (!workflowCalculation || !workflowCalculation.hasAlternativePartDescription) return false;
  const nonStockSupplier = nonStockSuppliers.find((x) => x.supplierId === supplierId);
  return !!nonStockSupplier && !isInStock && (description == null || description === '');
};

export const DASH = '—';

const NO_ADDITION = { fixedValue: 0, percentageValue: 0, marginValue: 0 };

export const displayAmount = (value: number, country: IReferential | null): string =>
  Utils.countryNumberFormat(Utils.Round(value).toFixed(2), country);

export const displayCurrency = (
  value: number,
  country: IReferential | null,
  currencyName: string
): string => `${displayAmount(value, country)} ${currencyName}`;

export const displayPercent = (value: number, country: IReferential | null): string =>
  `${displayAmount(value, country)}%`;

// Minimum variants offered per part when the variants feature is on.
export const DEFAULT_OPTIMAL_VARIANTS = 3;

export const alternativeCode = (alt: CaseCalculationAlternativePart): string =>
  alt.alternativeGuideNumber || '';

export const alternativeQuality = (alt: CaseCalculationAlternativePart): string => {
  if (alt.alternativePartQuality && alt.alternativePartQuality.code) {
    return alt.alternativePartQuality.code;
  }
  return alt.alternativePartQualityName || '';
};

export const alternativeSupplierName = (alt: CaseCalculationAlternativePart): string =>
  alt.supplierName || (alt.supplier && alt.supplier.name) || '';

export const alternativeUnitPrice = (alt: CaseCalculationAlternativePart): number =>
  alt.alternativePartDiscount == null
    ? alt.alternativePrice
    : alt.alternativePrice * (1 - alt.alternativePartDiscount / 100);

export const alternativeDifference = (
  part: CaseCalculationPart,
  alt: CaseCalculationAlternativePart
): number => {
  const pricedVariant = {
    ...part,
    ...mapAlternativePartToCalculationPart(alt, null, alt.excludeFromOrder)
  } as CaseCalculationPart;
  const original = new CalculationParts([], [part], 0, 0, NO_ADDITION, '', true);
  const alternative = new CalculationParts([], [pricedVariant], 0, 0, NO_ADDITION, '', false);

  return alternative.GetTotalPrice(pricedVariant) - original.GetTotalPrice(part);
};

export const queriedAlternatives = (part: CaseCalculationPart): CaseCalculationAlternativePart[] =>
  part.hasBeenInterogated ? part.alternativeParts : [];

export const resolveAlternative = (
  part: CaseCalculationPart,
  id: number
): CaseCalculationAlternativePart | undefined => part.alternativeParts.find((a) => a.id === id);

// Distinct suppliers (by supplierId) that returned at least one result for this part.
export const queriedSuppliersCount = (part: CaseCalculationPart): number =>
  _.uniqBy(queriedAlternatives(part), 'supplierId').length;

export const selectedAlternatives = (part: CaseCalculationPart): CaseCalculationAlternativePart[] =>
  part.alternativeParts.filter((a) => a.isVariantSelected);

export const offerVariants = (part: CaseCalculationPart): CaseCalculationAlternativePart[] =>
  selectedAlternatives(part).filter((a) => !a.isChild);

export const isBelowMinVariants = (part: CaseCalculationPart): boolean => {
  const selected = offerVariants(part).length;
  const available = part.alternativeParts.filter((a) => !a.isChild).length;
  return (
    selected > 0 && selected < DEFAULT_OPTIMAL_VARIANTS && available >= DEFAULT_OPTIMAL_VARIANTS
  );
};

// A parent variant's bundle children — same bundle, different code.
export const getChildrenParts = (
  alternativeParts: CaseCalculationAlternativePart[],
  parent: CaseCalculationAlternativePart
): CaseCalculationAlternativePart[] =>
  alternativeParts.filter(
    (a) =>
      a.isChild &&
      a.bundleNo != null &&
      a.bundleNo === parent.bundleNo &&
      a.alternativePartCode !== parent.alternativePartCode
  );

// Non-child variants with a delivery date; when includeChild, fold bundle children into the parent price.
export const getParentParts = (
  part: CaseCalculationPart,
  includeChild: boolean
): CaseCalculationAlternativePart[] => {
  const alternativeParts = part.alternativeParts;
  const parentParts = _.cloneDeep(alternativeParts).filter(
    (object) => !object.isChild && object.expectedDeliveryDate != null
  );
  if (includeChild) {
    for (let i = 0; i < parentParts.length; i++) {
      const childParts = getChildrenParts(alternativeParts, parentParts[i]);
      parentParts[i].alternativePrice =
        parentParts[i].alternativePrice + _.sumBy(childParts, 'alternativePrice');
    }
  }
  return parentParts;
};

// The offer that drives the part's amount — the primary non-child variant.
export const selectedParent = (
  part: CaseCalculationPart
): CaseCalculationAlternativePart | undefined =>
  part.alternativeParts.find((a) => a.isSelectedAlternativePart && !a.isChild);

export const markAlternativeGroup = (
  alternativeParts: CaseCalculationAlternativePart[],
  groupIds: number[],
  values: VariantSelectionState
): CaseCalculationAlternativePart[] =>
  alternativeParts.map((a) => (groupIds.indexOf(a.id) !== -1 ? { ...a, ...values } : a));

// Clears selection + recommendation on every variant (single-select in phase 1).
export const deselectAllVariants = (
  alternativeParts: CaseCalculationAlternativePart[]
): CaseCalculationAlternativePart[] =>
  alternativeParts.map((a) => ({
    ...a,
    isVariantSelected: false,
    isSelectedAlternativePart: false,
    groupedUniqueId: null
  }));

// Only one group can be recommended at a time — clear the flag off every variant first.
export const clearRecommendationFlags = (
  alternativeParts: CaseCalculationAlternativePart[]
): CaseCalculationAlternativePart[] =>
  alternativeParts.map((a) =>
    a.isSelectedAlternativePart ? { ...a, isSelectedAlternativePart: false } : a
  );

export const savingPct = (original: number, alternative: number) => {
  if (!original) {
    return 0;
  }
  const pct = (alternative / original - 1) * 100 * -1;

  return isNaN(pct) ? 0 : pct;
};

export const getPackageFromCalculationParts = (
  calculationParts: CaseCalculationPart[]
): CaseCalculationPart[] => {
  const result: CaseCalculationPart[] = [];
  calculationParts.forEach((part) => {
    (part.package ?? []).forEach((packagePart: CaseCalculationPart) => {
      result.push({
        ...packagePart,
        availableQuantity: part.availableQuantity,
        isInStock: part.isInStock
      });
    });
  });
  return result;
};

export const computeTotals = (
  calculationSectionList: IReferential[],
  calculationParts: CaseCalculationPart[],
  tva: number,
  smallPartsPercent: number,
  calculationTypeCode: string,
  currencyCode: string
): CalculationTotals => {
  const original = new CalculationParts(
    calculationSectionList,
    calculationParts,
    tva,
    smallPartsPercent,
    NO_ADDITION,
    calculationTypeCode,
    true
  );
  original.CurrencyCode = currencyCode;

  const alternative = new CalculationParts(
    calculationSectionList,
    calculationParts.concat(getPackageFromCalculationParts(calculationParts)),
    tva,
    smallPartsPercent,
    NO_ADDITION,
    calculationTypeCode,
    false
  );
  alternative.CurrencyCode = currencyCode;

  const originalAmountWithoutTVA = original.TotalWithoutVAT();
  const alternativeAmountWithoutTVA = alternative.TotalWithoutVAT();
  const originalPartsAmount = original.GetAmountPartWithDiscount(CalculationSectionCodeEnum.PART);
  const alternativePartsAmount = alternative.GetAmountPartWithDiscount(
    CalculationSectionCodeEnum.PART
  );
  const originalSmallPartsAmount = original.GetSmallPartsValue();
  const alternativeSmallPartsAmount = alternative.GetSmallPartsValue();

  return {
    originalPartsAmount,
    alternativePartsAmount,
    originalAmountWithoutTVA,
    alternativeAmountWithoutTVA,
    originalSmallPartsAmount,
    alternativeSmallPartsAmount,
    partsSavingPct: savingPct(originalPartsAmount, alternativePartsAmount),
    smallPartsSavingPct: savingPct(originalSmallPartsAmount, alternativeSmallPartsAmount),
    totalSavingPct: savingPct(originalAmountWithoutTVA, alternativeAmountWithoutTVA),
    savingValue: originalAmountWithoutTVA - alternativeAmountWithoutTVA
  };
};

const COLUMN_TYPE: { [key in ColumnKey]?: ColumnType } = GRID_COLUMNS.reduce(
  (map, c) => ({ ...map, [c.key]: c.type }),
  {} as { [key in ColumnKey]?: ColumnType }
);

export const getCellValue = (
  part: CaseCalculationPart,
  alt: CaseCalculationAlternativePart,
  key: ColumnKey
): string | number | null => {
  switch (key) {
    case ColumnKey.Code:
      return alternativeCode(alt);
    case ColumnKey.Name:
      return alt.name;
    case ColumnKey.NetPrice:
      return alt.alternativeNetPrice;
    case ColumnKey.Price:
      return alternativeUnitPrice(alt);
    case ColumnKey.Quality:
      return alternativeQuality(alt);
    case ColumnKey.Delivery:
      return alt.expectedDeliveryDate;
    case ColumnKey.Producer:
      return alt.producer;
    case ColumnKey.Supplier:
      return alternativeSupplierName(alt);
    case ColumnKey.Quantity:
      return alt.availableQuantity;
    case ColumnKey.Location:
      return alt.stockLocation;
    case ColumnKey.Difference:
      return alternativeDifference(part, alt);
    default:
      return '';
  }
};

const NUMERIC_COLUMNS: ColumnKey[] = [ColumnKey.Price, ColumnKey.Quantity, ColumnKey.Difference];

const forEachFilteredAlternative = (
  parts: CaseCalculationPart[],
  filters: FiltersState,
  visit: (part: CaseCalculationPart, alt: CaseCalculationAlternativePart) => void
): void => {
  const matches = compileFilters(filters);
  parts.forEach((part) =>
    queriedAlternatives(part).forEach((alt) => {
      if (matches(part, alt)) {
        visit(part, alt);
      }
    })
  );
};

export const uniqueValues = (
  parts: CaseCalculationPart[],
  key: ColumnKey,
  filters: FiltersState = {}
): string[] => {
  const set = new Set<string>();
  forEachFilteredAlternative(parts, filters, (part, alt) => {
    const value = String(getCellValue(part, alt, key) ?? '').trim();
    if (value) set.add(value);
  });
  return Array.from(set).sort();
};

const distinctObjects = <T>(
  parts: CaseCalculationPart[],
  filters: FiltersState,
  pick: (alt: CaseCalculationAlternativePart) => T | null | undefined,
  keyOf: (value: T) => string
): T[] => {
  const seen: { [key: string]: true } = {};
  const list: T[] = [];
  forEachFilteredAlternative(parts, filters, (_part, alt) => {
    const value = pick(alt);
    const k = value ? keyOf(value) : '';
    if (value && k && !seen[k]) {
      seen[k] = true;
      list.push(value);
    }
  });
  return list;
};

interface ObjectFilterAccessor {
  distinct: (parts: CaseCalculationPart[], filters: FiltersState) => ColumnFilter[];
  label: (option: ColumnFilter) => string;
  match: (option: ColumnFilter) => string;
}

const OBJECT_FILTERS: { [key in ColumnKey]?: ObjectFilterAccessor } = {
  [ColumnKey.Quality]: {
    distinct: (parts, filters) =>
      distinctObjects(
        parts,
        filters,
        (a) => a.alternativePartQuality,
        (q) => q.code || ''
      ),
    label: (o) => (o as IReferential).code || '',
    match: (o) => (o as IReferential).code || ''
  },
  [ColumnKey.Supplier]: {
    distinct: (parts, filters) =>
      distinctObjects(
        parts,
        filters,
        (a) => a.supplier,
        (s) => s.name
      ),
    label: (o) => (o as Organization).name || '',
    match: (o) => (o as Organization).name || ''
  }
};

// Options cascade: a column's values come only from alternatives that pass the other active filters.
export const filterOptionsFor = (
  parts: CaseCalculationPart[],
  key: ColumnKey,
  filters: FiltersState = {}
): ColumnFilter[] => {
  const otherFilters = { ...filters };
  delete otherFilters[key];
  const accessor = OBJECT_FILTERS[key];
  return accessor ? accessor.distinct(parts, otherFilters) : uniqueValues(parts, key, otherFilters);
};

export const filterOptionLabel = (key: ColumnKey, option: ColumnFilter | null): string => {
  if (option == null) return '';
  const accessor = OBJECT_FILTERS[key];
  return accessor ? accessor.label(option) : String(option);
};

export const filterMatchString = (key: ColumnKey, filter: ColumnFilter): string => {
  if (typeof filter === 'string') return filter;
  if (isRangeFilter(filter)) return '';
  const accessor = OBJECT_FILTERS[key];
  return accessor ? accessor.match(filter) : String(filter);
};

export type FilterPredicate = (
  part: CaseCalculationPart,
  alt: CaseCalculationAlternativePart
) => boolean;

// Pre-compiles the active filters once (targets/bounds resolved up front) so the per-alternative
// check is just field reads and comparisons — no re-parsing the filter for every row.
export const compileFilters = (filters: FiltersState): FilterPredicate => {
  const tests: FilterPredicate[] = [];
  (Object.keys(filters) as ColumnKey[]).forEach((key) => {
    const filter = filters[key];
    if (!filter) return;
    const type = COLUMN_TYPE[key];
    if (type === ColumnType.Range && isRangeFilter(filter)) {
      const { min, max } = filter;
      tests.push((part, alt) => {
        const num = Number(getCellValue(part, alt, key));
        return (min == null || num >= min) && (max == null || num <= max);
      });
    } else if (type === ColumnType.Date && isDateRangeFilter(filter)) {
      const from = filter.from ? new Date(filter.from).getTime() : null;
      const to = filter.to ? new Date(`${filter.to}T23:59:59`).getTime() : null;
      tests.push((part, alt) => {
        const value = getCellValue(part, alt, key);
        const time = value ? new Date(String(value)).getTime() : NaN;
        return !isNaN(time) && (from == null || time >= from) && (to == null || time <= to);
      });
    } else {
      const target = filterMatchString(key, filter).toLowerCase();
      const exact = type === ColumnType.Select;
      tests.push((part, alt) => {
        const cell = String(getCellValue(part, alt, key)).toLowerCase();
        return exact ? cell === target : cell.includes(target);
      });
    }
  });
  return (part, alt) => tests.every((test) => test(part, alt));
};

export const compareByExpectedDelivery = (
  a: CaseCalculationAlternativePart,
  b: CaseCalculationAlternativePart
): number => {
  const time = (value: string | null) => (value ? new Date(value).getTime() : Infinity);
  return time(a.expectedDeliveryDate) - time(b.expectedDeliveryDate);
};

export const sortAlternatives = (
  part: CaseCalculationPart,
  alts: CaseCalculationAlternativePart[],
  sort: SortState
): CaseCalculationAlternativePart[] => {
  if (!sort.column) return alts;
  const key = sort.column;
  const isDate = COLUMN_TYPE[key] === ColumnType.Date;
  const isNumeric = NUMERIC_COLUMNS.indexOf(key) !== -1;
  const sortKey = (alt: CaseCalculationAlternativePart): number | string => {
    const value = getCellValue(part, alt, key);
    if (isDate) return value ? new Date(String(value)).getTime() : Infinity;
    if (isNumeric) return Number(value);
    return String(value).toLowerCase();
  };
  // _.orderBy computes the sort key once per item and keeps the sort stable.
  return _.orderBy(alts, [sortKey], [sort.dir === 1 ? 'asc' : 'desc']);
};

export const EMPTY_TOTALS: CalculationTotals = {
  originalPartsAmount: 0,
  alternativePartsAmount: 0,
  originalAmountWithoutTVA: 0,
  alternativeAmountWithoutTVA: 0,
  originalSmallPartsAmount: 0,
  alternativeSmallPartsAmount: 0,
  partsSavingPct: 0,
  smallPartsSavingPct: 0,
  totalSavingPct: 0,
  savingValue: 0
};

export const INITIAL_STATE: AlternativeCalculationDialogState = {
  caseCalculationHistoryDetails: {} as CaseCalculationHistory,
  isLoading: true,
  workflowCalculation: null,
  workflowOptimization: [],
  openMenuItem: null,
  TVA: '',
  calculationSectionList: [],
  calculationPartsOrdered: [],
  packageCalculationParts: [],
  orderPartList: [],
  partQualityTypes: [],
  maintenancePackages: [],
  supplierList: [],
  nonStockSuppliers: [],
  partSection: null,
  caseStatuses: [],
  ownStockOrganization: null,
  organizationBranches: [],
  selectedOrganizationBranch: null,
  currentWorkflowContainsOrderFormOrInterogatePartner: false,
  hasBillingForm: false,
  carClassRentPrice: null,
  rentVehiclePrices: null
};

// One bundle child -> a package line on its parent (used by pushBundleChildrenToPackage).
export const buildBundlePackagePart = (
  part: CaseCalculationPart,
  child: CaseCalculationAlternativePart,
  groupedUniqueId: string | null
): CaseCalculationPart =>
  ({
    ...part,
    ...mapAlternativePartToCalculationPart(child, groupedUniqueId, true),
    id: 0,
    originalPrice: 0,
    package: null
  } as CaseCalculationPart);

export const revertAlternativeMapping = <T extends CaseCalculationPart>(part: T): T => ({
  ...part,
  alternativePrice: null,
  alternativePartDiscount: null,
  alternativeGuideNumber: null,
  alternativePartCode: null,
  alternativePartName: null,
  alternativePartQuality: null,
  alternativePartQualityId: null,
  alternativePartStockLocation: null,
  alternativePartStockQuantity: null,
  alternativePartRemarks: null,
  minQty: 0,
  packing: null,
  bundleItem: null,
  bundleNo: null,
  bundleQuantity: null,
  supplierId: null,
  supplier: null,
  producer: '',
  deliveryTime: '',
  alternativeNetPrice: null,
  alternativePriceWithoutDiscount: null,
  package: [],
  groupedUniqueId: null
});

export const partitionEditedCalculationPart = (
  calculationParts: CaseCalculationPart[],
  groupedUniqueId: string | null,
  caseCalculationPartId: number
): PartitionedCalculationParts => {
  const calculationPartsCopy = calculationParts.filter(
    (item) =>
      (item.groupedUniqueId !== null && item.groupedUniqueId !== groupedUniqueId) ||
      (item.groupedUniqueId === null && item.id !== caseCalculationPartId)
  );
  const selectedCalculationPart = calculationParts.find(
    (item) => item.groupedUniqueId === groupedUniqueId && item.id === caseCalculationPartId
  );

  return { calculationPartsCopy, selectedCalculationPart };
};

export const isNonStockPartSelected = (
  part: CaseCalculationPart,
  nonStockSuppliers: WorkflowSupplier[]
): boolean =>
  nonStockSuppliers.some((supplier) => supplier.supplierId === part.supplierId) && !part.isInStock;
